import { GetStaticPropsResult, GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'
import { ParsedUrlQuery } from 'querystring'
import { useContext } from 'react'

import { productPageModuleQuery } from '@data/sanity/queries/page'
import {
  SanityModuleType,
  SanityProductHeroModule,
} from '@data/sanity/queries/types/modules'
import { SanityProductPage } from '@data/sanity/queries/types/page'
import { SanityProductCatalogueQuery } from '@data/sanity/queries/types/product'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getShopifyDomain } from '@lib/shopify/client'
import { getPageSanityClient, getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getAllProductSlugs,
} from '@lib/sanity/page'
import { getProductCatalogue, getProductPage } from '@lib/sanity/product'
import { useViewContentFacebookEvent } from '@lib/facebook'
import { getAllLocales, Locale } from '@lib/language'
import { useVariantMetadata } from '@lib/metadata'
import { useDigitalProductContent } from '@lib/product'
import { ProductContext } from '@lib/product-context'
import {
  getProductReviews,
  getProductReviewSummary,
  getReviews,
  ProductReview,
  ProductReviewSummary,
  ReviewProvider,
  Reviews,
} from '@lib/review'
import { CustomStaticPropsContext, StaticPathWithSlug } from '@lib/routes'
import { getProductSchema } from '@lib/schema'
import { ShopContext } from '@lib/shop'

import Layout from '@components/layout'
import Modules from '@modules/modules'
import PreviewModules from '@modules/preview-modules'

const PreviewProvider = dynamic(() => import('@lib/sanity/preview'))

interface ProductPageProps {
  draftMode: boolean
  draftToken?: string
  locale: Locale
  slug: string
  page: SanityProductPage
  site: SanitySiteFragment
  productCatalogue: SanityProductCatalogueQuery
  reviews: Reviews
  productReviews: ProductReview[]
  productReviewSummary: ProductReviewSummary
}

interface StaticProductPageParams extends ParsedUrlQuery {
  slug: string
}

/**
 * The product page.
 */
const ProductPage = ({
  draftMode,
  draftToken,
  locale,
  slug,
  page,
  site,
  productCatalogue,
  reviews,
  productReviews,
  productReviewSummary,
}: ProductPageProps) => {
  const { currencyCode, taxRate } = useContext(ShopContext)
  const { product, activeVariant } = useContext(ProductContext)

  // Get product schema
  const productSchema = getProductSchema(
    productReviews,
    productReviewSummary,
    product,
    activeVariant,
    site.seo?.siteTitle,
    site.settings.siteURL,
    currencyCode,
    taxRate,
  )

  // Load the correct digital product content
  const [showPrivateContent, isLoaded] = useDigitalProductContent(
    productCatalogue,
    slug,
  )

  // Trigger view content Facebook event
  useViewContentFacebookEvent(
    locale,
    product?.title,
    activeVariant?.id,
    activeVariant?.options,
    site.settings?.facebookEvents,
  )

  // Set product variant metadata
  useVariantMetadata(activeVariant)

  if (draftMode && draftToken) {
    return (
      <Layout page={page} site={site} productSchema={productSchema}>
        <PreviewProvider draftToken={draftToken}>
          <PreviewModules
            modules={page.modules}
            moduleQuery={productPageModuleQuery}
            moduleQueryParams={{
              ...defaultModuleQueryParameters,
              locale,
              productSlug: slug,
            }}
            reviews={reviews}
          />
        </PreviewProvider>
      </Layout>
    )
  }

  if (!isLoaded) {
    return null
  }

  return (
    <Layout page={page} site={site} productSchema={productSchema}>
      <Modules
        modules={showPrivateContent ? page.privateModules : page.modules}
        reviews={reviews}
      />
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext<StaticProductPageParams>,
): Promise<GetStaticPropsResult<ProductPageProps>> {
  const [sanityClient, draftMode, draftToken] = getPageSanityClient(
    context.draftMode,
  )
  const slug = context.params?.slug ?? ''
  const productPage = await getProductPage(sanityClient, context.locale, slug)

  // Get product from product hero module
  const pageModules = productPage.page?.modules ?? []
  const productHeroModule = pageModules.find(
    ({ _type }) => _type === SanityModuleType.PRODUCT_HERO,
  ) as SanityProductHeroModule | undefined
  const product = productHeroModule?.product

  if (!productPage.page || !product) {
    return {
      notFound: true,
    }
  }

  // Get basic information for all products
  const productCatalogue = await getProductCatalogue(
    sanityClient,
    context.locale,
  )

  // Get Stamped.io reviews
  let reviews: Reviews = {
    rating: 0,
    items: [],
  }
  let productReviews: ProductReview[] = []
  let productReviewSummary: ProductReviewSummary = {
    ratingCount: 0,
  }

  try {
    const shopifyDomain = getShopifyDomain(context.locale)
    const stampedApiKey = productPage.site.settings.stampedApiKey
    reviews = await getReviews(
      pageModules,
      shopifyDomain,
      stampedApiKey,
      product.id,
    )
    productReviews = await getProductReviews(shopifyDomain, product.id, {
      provider: ReviewProvider.STAMPED,
      apiKey: stampedApiKey,
    })
    productReviewSummary = await getProductReviewSummary(
      shopifyDomain,
      product.id,
      {
        provider: ReviewProvider.STAMPED,
        apiKey: stampedApiKey,
      },
    )
  } catch (error) {
    console.error(error)
  }

  return {
    props: {
      draftMode,
      draftToken,
      locale: context.locale,
      slug,
      page: productPage.page,
      site: productPage.site,
      productCatalogue,
      reviews,
      productReviews,
      productReviewSummary,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticProductPageParams>
> {
  const sanityClient = getSanityClient()
  const allProducts = await getAllProductSlugs(sanityClient)
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlug[] = allProducts
    .map((product) => ({
      ...product,
      locale: product.locale as Locale,
    }))
    .filter(({ locale }) => allLocales.includes(locale))
    .filter(({ slug }) => !!slug)
    .map(({ locale, slug }) => ({
      params: {
        slug,
      },
      locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default ProductPage
